import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { ArrowLeft, BookOpen, Printer, Map, ArrowRight, Users } from 'lucide-react';
import { toast } from 'sonner';
import PracticeScenario from '../shared/PracticeScenario';
import PageMessaging from '../shared/PageMessaging';

export default function ConnectionRoadmap() {
  const navigate = useNavigate();
  const printPage = () => { window.print(); toast.success('Print dialog opened'); };

  const levels = [
    {
      level: 1,
      title: 'Surface Level',
      subtitle: 'Facts & Information',
      color: 'blue',
      description: 'Exchange of basic information, small talk, and factual observations.',
      examples: [
        'Weather, news, events',
        'Work updates, schedules',
        'What happened, who did what'
      ],
      tips: 'Start here to establish safety and rapport. Don\'t skip this level.'
    },
    {
      level: 2,
      title: 'Feelings Level',
      subtitle: 'Emotional Expression',
      color: 'amber',
      description: 'Sharing how we feel about what\'s happening in our lives.',
      examples: [
        'I feel excited about the project',
        'I\'m feeling overwhelmed lately',
        'That made me happy/sad/frustrated'
      ],
      tips: 'Create safety for vulnerability. Listen without fixing or advising.'
    },
    {
      level: 3,
      title: 'Needs Level',
      subtitle: 'Deep Connection',
      color: 'green',
      description: 'Sharing our universal human needs and what matters most to us.',
      examples: [
        'I really need connection right now',
        'What I value most is honesty',
        'My need for autonomy is strong'
      ],
      tips: 'This is where true intimacy lives. Trust must be established first.'
    }
  ];

  const roadmapSteps = [
    {
      step: 1,
      title: 'Start with Presence',
      description: 'Ground yourself. Take a breath. Set an intention to connect.'
    },
    {
      step: 2,
      title: 'Meet Them Where They Are',
      description: 'Notice what level the other person is at. Match their energy before going deeper.'
    },
    {
      step: 3,
      title: 'Create Safety',
      description: 'Use empathy and reflection. Show you\'re listening without judgment.'
    },
    {
      step: 4,
      title: 'Invite Deeper Sharing',
      description: 'Ask feeling and need questions: "How did that feel?" "What matters to you about this?"'
    },
    {
      step: 5,
      title: 'Share Vulnerably',
      description: 'Model the depth you want by sharing your own feelings and needs.'
    },
    {
      step: 6,
      title: 'Celebrate Connection',
      description: 'Acknowledge the quality of connection you\'ve created together.'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <style>{`@media print { body { background: white !important; } .no-print { display: none !important; } .print-page { background: white !important; color: black !important; } }`}</style>
      
      <div className="border-b border-slate-700/50 bg-slate-900/90 backdrop-blur-sm sticky top-0 z-50 no-print">
        <div className="container mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button onClick={() => navigate('/belonging-ebook')} variant="outline" size="sm" className="border-slate-600 hover:border-slate-500 text-white hover:bg-slate-800">
                <ArrowLeft className="h-4 w-4 mr-2" />Back to Workbook
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-white">Connection Roadmap</h1>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button onClick={printPage} variant="outline" size="sm" className="border-blue-600 hover:bg-blue-900/20 text-white">
                <Printer className="h-4 w-4 mr-2" />Print
              </Button>
              <div className="flex items-center gap-2 text-slate-400 text-sm">
                <BookOpen className="h-4 w-4" />
                <span>Page 45</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-12 print-page">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <Map className="h-16 w-16 text-green-400 mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-white mb-4">Connection Roadmap</h2>
            <p className="text-slate-300 max-w-2xl mx-auto">A guide to deepening connection through the three levels of conversation.</p>
          </div>

          <h3 className="text-2xl font-bold text-white mb-6">The Three Levels of Connection</h3>
          <div className="space-y-4 mb-12">
            {levels.map((level, index) => (
              <div key={index} className={`bg-${level.color}-900/20 border border-${level.color}-700/30 rounded-lg p-6`} style={{ backgroundColor: level.color === 'blue' ? 'rgba(37, 99, 235, 0.1)' : level.color === 'amber' ? 'rgba(217, 119, 6, 0.1)' : 'rgba(22, 163, 74, 0.1)' }}>
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold flex-shrink-0`} style={{ backgroundColor: level.color === 'blue' ? '#2563eb' : level.color === 'amber' ? '#d97706' : '#16a34a' }}>
                    {level.level}
                  </div>
                  <div className="flex-1">
                    <h4 className="text-xl font-bold text-white mb-1">{level.title}</h4>
                    <p className="text-slate-400 text-sm mb-3">{level.subtitle}</p>
                    <p className="text-slate-300 mb-4">{level.description}</p>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="bg-slate-900/50 rounded-lg p-3">
                        <p className="text-sm font-medium text-slate-400 mb-2">Examples:</p>
                        <ul className="space-y-1">
                          {level.examples.map((ex, i) => (
                            <li key={i} className="text-slate-300 text-sm">• {ex}</li>
                          ))}
                        </ul>
                      </div>
                      <div className="bg-slate-900/50 rounded-lg p-3">
                        <p className="text-sm font-medium text-slate-400 mb-2">💡 Tip:</p>
                        <p className="text-slate-300 text-sm">{level.tips}</p>
                      </div>
                    </div>
                  </div>
                </div>
                {index < levels.length - 1 && (
                  <div className="flex justify-center mt-4">
                    <ArrowRight className="h-6 w-6 text-slate-500 rotate-90" />
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="bg-purple-900/20 border border-purple-700/30 rounded-lg p-6 mb-10">
            <h3 className="text-xl font-bold text-purple-200 mb-4 flex items-center gap-2">
              <Users className="h-5 w-5" /> The Roadmap: Steps to Deeper Connection
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              {roadmapSteps.map((step, index) => (
                <div key={index} className="bg-slate-900/50 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center text-white font-bold text-sm">{step.step}</span>
                    <h4 className="font-bold text-purple-200">{step.title}</h4>
                  </div>
                  <p className="text-slate-300 text-sm">{step.description}</p>
                </div>
              ))}
            </div>
          </div>

          <PracticeScenario pageTitle="Connection Roadmap" pageNumber={45} />
          <PageMessaging pageTitle="Connection Roadmap" pageNumber={45} />
        </div>
      </div>
    </div>
  );
}
